/**
 * historicalState — time-machine reconstruction of map ownership +
 * army counts at any point in a game's event log.
 *
 * The engine persists an `initialTerritories` snapshot at game start
 * (apps/api/src/routes/games.ts /start handler) AND maintains an
 * append-only `events` log. Together those two are enough to replay
 * the map state at any point in time — `reconstructTerritoriesAt`
 * walks the events forward and applies each one's MAP effect.
 *
 * Cards / decks / hand state are intentionally out of scope. Card
 * draws + turn-ins do not carry the drawn-card ids in their event
 * payloads, so a faithful card replay would require a schema change.
 * Since this feature only renders the map, that's fine — we skip
 * card-related events entirely.
 *
 * Pure: no React, no DB, no clock. Suitable for direct unit testing.
 */

import type {
  GameEvent,
  PlayerId,
  TerritoryId,
} from '@warcarp/api-types'
import { applyEventToMap, type HistoricalTerritory } from '@warcarp/api-types'
// The board replay is shared with the server (fog-of-war snapshots), so it
// lives in packages/api-types/src/replay.ts.
export { reconstructTerritoriesAt, type HistoricalTerritory } from '@warcarp/api-types'

/**
 * What to highlight on the map when viewing a snapshot at this event.
 *
 * - `primaryId` / `secondaryId` map to MapView's `selectedTerritoryId` and
 *   `secondaryTerritoryId` props so the existing two-tone selection
 *   chrome works without any new MapView styling.
 * - `haloIds` covers multi-target events (a placement across three
 *   territories) — forwarded to MapView's `previewTargetIds`, which rings the
 *   troop number of each one. Ringing is the right shape for "these are the
 *   cells this event touched": it survives any fill, it reads on a 20 px cell,
 *   and it's the same mark the attack/fortify phases use for "these are the
 *   cells in play", so the board says one thing one way. It replaced a white
 *   cross-hatch, which on a placement was easy to miss and easy to confuse with
 *   the regions overlay's adjacency texture.
 * - `attackSnapshot` is the per-attack "−N, −M" pair you'd see for
 *   half a second in the live game; in snapshot view it renders
 *   frozen via MapView's new `staticAttackBadge` prop.
 */
export interface EventHighlight {
  primaryId?: TerritoryId
  secondaryId?: TerritoryId
  haloIds?: TerritoryId[]
  /**
   * Override for MapView's `selectedFillMode`. Defaults to 'lighten'
   * across the board so highlighted territories still read as owned
   * by a given player. Placements override to 'amber' so the
   * placed-into territory pops against the player's own colour —
   * a darkened-own-colour fill is too subtle for the user to spot
   * which territory in their own back-line received troops.
   */
  fillMode?: 'amber' | 'lighten'
  attackSnapshot?: {
    fromTerritoryId: TerritoryId
    toTerritoryId: TerritoryId
    attackerLost: number
    defenderLost: number
    conquered: boolean
    /** Engine PlayerId (slot string) of the attacker. Caller maps → colour. */
    attackerPlayerId: PlayerId
    /** Engine PlayerId of the defender, or '' when defender was neutral. */
    defenderPlayerId: PlayerId
  }
  /**
   * Static conquest ring data — only set on a conquest event (or an
   * attack event with `conquered: true`). Caller maps `ownerPlayerId`
   * to a colour and passes the result to MapView via
   * `staticConquestRing`.
   */
  conquestRing?: {
    territoryId: TerritoryId
    /** Engine PlayerId (slot string) of the new owner (the attacker). */
    ownerPlayerId: PlayerId
  }
  /**
   * Static fortify trail data — only set on a fortify event. Caller
   * maps `playerId` to a colour and passes the result to MapView via
   * `staticFortifyEntry`.
   */
  fortifySnapshot?: {
    fromTerritoryId: TerritoryId
    toTerritoryId: TerritoryId
    armies: number
    /** Engine PlayerId (slot string) of the fortifying player. */
    playerId: PlayerId
  }
}

/**
 * Find the `attack` event that produced the conquest at `eventIndex`.
 *
 * The engine always emits attack + conquest events back-to-back with
 * identical timestamps; `conquest` is never standalone. We scan
 * backwards a few entries to find the matching attack on the same
 * destination territory. Returns null if we can't find one (legacy
 * games or unusual logs).
 */
function lookbackForAttack(
  events: GameEvent[],
  conquestIndex: number,
  toTerritoryId: TerritoryId,
): Extract<GameEvent, { kind: 'attack' }> | null {
  // Bound the scan — bundles are typically 2-4 entries. 8 is generous.
  const minIndex = Math.max(0, conquestIndex - 8)
  for (let i = conquestIndex - 1; i >= minIndex; i--) {
    const ev = events[i]
    if (!ev) continue
    if (ev.kind === 'attack' && ev.toTerritoryId === toTerritoryId) {
      return ev
    }
    // Stop scanning if we cross a turn-start — the conquest must have
    // come from this turn's attack chain.
    if (ev.kind === 'turn-start') break
  }
  return null
}

/**
 * Derive the territory + attack-badge highlights for the event at the
 * given index. Returns null when the event doesn't touch the map
 * (turn-start, phase-advance, card-*, elimination, game-end).
 */
export function getEventHighlight(
  events: GameEvent[] | undefined,
  eventIndex: number,
): EventHighlight | null {
  if (!events) return null

  // Step back over trailing phase-advances before deciding what to highlight.
  //
  // A log ROW carries the index of the LAST event in its bundle, because that
  // is what the time machine must replay to show the board after the row's
  // action. `phase-advance` is dropped from the log entirely (formatEventLine
  // returns null), so it is never the row's subject — but it very often IS the
  // last event in the bundle. The reinforce route emits placement then
  // phase-advance back to back, which meant every "places N armies" row asked
  // for the highlight of a phase-advance and got nothing at all. Same for a
  // fortify that ends the turn.
  //
  // Bounded to a handful of steps: bundles are 2-4 entries, and phase-advance
  // is the only kind that can trail like this, so this can never walk far
  // enough to highlight the previous action.
  let idx = eventIndex
  for (let guard = 0; guard < 4 && events[idx]?.kind === 'phase-advance'; guard++) {
    idx -= 1
  }
  const ev = events[idx]
  if (!ev) return null

  switch (ev.kind) {
    case 'claim':
      // Claim sits on the player's own colour (just-acquired
      // territory). The default 'lighten' fill darkens that colour
      // by 40 % which reads as the same colour at a glance — switch
      // to the bright amber selection chrome so the territory pops.
      return { primaryId: ev.territoryId, fillMode: 'amber' }
    case 'placement': {
      if (ev.placements.length === 0) return null
      // Halo ONLY — no `primaryId`, so no territory fill. A placement is not a
      // selection: nothing changed hands, armies just landed, and the count is
      // the entire story. Repainting the cell amber shouted about the territory
      // when the thing that changed is the NUMBER, and it could only ever
      // shout about one of them — a placement across three cells got one amber
      // fill and two unmarked neighbours. The halo marks every one of them,
      // equally, right where the change is.
      return { haloIds: ev.placements.map((p) => p.territoryId) }
    }
    case 'attack':
      return {
        primaryId: ev.fromTerritoryId,
        secondaryId: ev.toTerritoryId,
        attackSnapshot: {
          fromTerritoryId: ev.fromTerritoryId,
          toTerritoryId: ev.toTerritoryId,
          attackerLost: ev.attackerLost,
          defenderLost: ev.defenderLost,
          conquered: ev.conquered,
          attackerPlayerId: ev.attacker,
          defenderPlayerId: ev.defender,
        },
        // Attack with conquered=true also wears the conquest ring —
        // ownership flipped to the attacker in this commit, so the
        // ring belongs on the captured territory.
        conquestRing: ev.conquered
          ? { territoryId: ev.toTerritoryId, ownerPlayerId: ev.attacker }
          : undefined,
      }
    case 'conquest': {
      // The conquest event itself doesn't carry the dice roll — its
      // matching attack event (always immediately before it in the
      // events array, identical ts) does. Look back to pull that
      // payload so the snapshot view can show the −N, −M badges
      // alongside the captured-territory ring.
      const prior = lookbackForAttack(events, idx, ev.territoryId)
      return {
        primaryId: ev.territoryId,
        ...(prior
          ? {
              secondaryId: prior.fromTerritoryId,
              attackSnapshot: {
                fromTerritoryId: prior.fromTerritoryId,
                toTerritoryId: prior.toTerritoryId,
                attackerLost: prior.attackerLost,
                defenderLost: prior.defenderLost,
                conquered: prior.conquered,
                attackerPlayerId: prior.attacker,
                defenderPlayerId: prior.defender,
              },
            }
          : {}),
        conquestRing: { territoryId: ev.territoryId, ownerPlayerId: ev.attacker },
      }
    }
    case 'post-conquest-transfer':
      // Free fortify — same from/to highlight + static trail chrome as a
      // regular fortify (teal line + arrowhead + "→N").
      return {
        primaryId: ev.fromTerritoryId,
        secondaryId: ev.toTerritoryId,
        fortifySnapshot: {
          fromTerritoryId: ev.fromTerritoryId,
          toTerritoryId: ev.toTerritoryId,
          armies: ev.armies,
          playerId: ev.player,
        },
      }
    case 'fortify':
      // Fortify gets the from/to highlight pair AND the static trail
      // chrome — same teal line + arrowhead + "→N" badge a live
      // fortify draws, frozen at full opacity.
      return {
        primaryId: ev.fromTerritoryId,
        secondaryId: ev.toTerritoryId,
        fortifySnapshot: {
          fromTerritoryId: ev.fromTerritoryId,
          toTerritoryId: ev.toTerritoryId,
          armies: ev.armies,
          playerId: ev.player,
        },
      }
    // No map-level highlight for these — turn-start / phase-advance /
    // card-* / elimination / game-end / resign-style events. Resign
    // touches every territory the player owned, which would highlight
    // half the map; better UX is to show no highlight and let the
    // ownership change speak for itself.
    case 'turn-start':
    case 'phase-advance':
    case 'elimination':
    case 'team-eliminated':
    case 'card-draw':
    case 'card-turn-in':
    case 'resign':
    case 'game-end':
    case 'ai-takeover':
    case 'hidden': // fog of war: nothing to point at for a move the viewer couldn't see
      return null
  }
}


/**
 * Total armies owned after each valid combat roll involving a player.
 *
 * This deliberately uses the same event cadence as Dice Luck's cumulative
 * series: one point after every attack where the player rolled as attacker or
 * defender. Placements and other players' actions are still replayed between
 * those points, so the next combat point reflects the player's real board
 * strength at that moment.
 */
export type PlayerArmySeries = Record<PlayerId, number[]>



export function buildPlayerArmySeries(
  initialTerritories: HistoricalTerritory[] | undefined,
  events: GameEvent[] | undefined,
  playerIds: readonly PlayerId[],
): PlayerArmySeries | null {
  if (!initialTerritories) return null

  const byId = new Map<TerritoryId, HistoricalTerritory>()
  for (const territory of initialTerritories) {
    byId.set(territory.id, { ...territory })
  }
  const series: PlayerArmySeries = Object.fromEntries(
    playerIds.map((playerId) => [playerId, []]),
  )

  const totalArmies = (playerId: PlayerId): number => {
    let total = 0
    for (const territory of byId.values()) {
      if (territory.ownerId === playerId) total += territory.armies
    }
    return total
  }

  for (const event of events ?? []) {
    applyEventToMap(byId, event)
    if (event.kind !== 'attack') continue
    if (
      !byId.has(event.fromTerritoryId) ||
      !byId.has(event.toTerritoryId) ||
      event.roll.attacker.length < 1 ||
      event.roll.defender.length < 1
    ) {
      continue
    }

    const involved = event.defender === ''
      ? [event.attacker]
      : event.attacker === event.defender
        ? [event.attacker]
        : [event.attacker, event.defender]
    for (const playerId of involved) {
      const playerSeries = series[playerId] ?? (series[playerId] = [])
      playerSeries.push(totalArmies(playerId))
    }
  }

  return series
}
